import { ThemedText } from "@/components/themed-text";
import { SheetTextInput } from "@/components/ui/sheet-text-input";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopSheetStyle } from "@/hooks/use-desktop-layout";
import { BottomSheetModal, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import Ionicons from "@react-native-vector-icons/ionicons";
import React, { forwardRef, useMemo, useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";

interface VerificationCodeModalProps {
  onClose: () => void;
  onSubmit: (code: string) => void;
  isSubmitting?: boolean;
}

export const VerificationCodeModal = forwardRef<
  BottomSheetModal,
  VerificationCodeModalProps
>(function VerificationCodeModal(
  { onClose, onSubmit, isSubmitting = false },
  ref,
) {
  const { theme } = useAuth();
  const snapPoints = useMemo(() => ["100%"], []);
  const desktopSheetStyle = useDesktopSheetStyle();
  const [code, setCode] = useState("");

  const handleClose = () => {
    setCode("");
    onClose();
  };

  const handleDismiss = () => {
    setCode("");
  };

  const handleSubmit = () => {
    if (code.length !== 6) {
      return;
    }

    onSubmit(code);
  };

  return (
    <BottomSheetModal
      ref={ref}
      index={0}
      snapPoints={snapPoints}
      style={desktopSheetStyle}
      enablePanDownToClose
      onDismiss={handleDismiss}
      backgroundStyle={{ backgroundColor: theme.background }}
      handleIndicatorStyle={styles.handle}
    >
      <BottomSheetScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <ThemedText type="subtitle" style={styles.title}>
            Verification Code
          </ThemedText>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <Ionicons name="close" size={20} color="#94a3b8" />
          </TouchableOpacity>
        </View>

        <ThemedText
          type="small"
          style={[styles.message, { color: theme.text + "90" }]}
        >
          Ask the customer for their 6-digit verification code to complete this
          booking.
        </ThemedText>

        <SheetTextInput
          value={code}
          onChangeText={(value) =>
            setCode(value.replace(/\D/g, "").slice(0, 6))
          }
          keyboardType="number-pad"
          maxLength={6}
          placeholder="000000"
          placeholderTextColor={theme.text}
          style={[
            styles.input,
            { borderColor: theme.border, color: theme.text },
          ]}
          editable={!isSubmitting}
          autoFocus
        />

        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.button, styles.cancelButton]}
            onPress={handleClose}
            disabled={isSubmitting}
          >
            <ThemedText type="defaultSemiBold" style={styles.cancelText}>
              Cancel
            </ThemedText>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.button,
              styles.submitButton,
              (code.length !== 6 || isSubmitting) && styles.buttonDisabled,
            ]}
            onPress={handleSubmit}
            disabled={code.length !== 6 || isSubmitting}
          >
            <Ionicons name="checkmark-circle" size={18} color="#fff" />
            <ThemedText style={styles.submitText} type="defaultSemiBold">
              Complete
            </ThemedText>
          </TouchableOpacity>
        </View>
      </BottomSheetScrollView>
    </BottomSheetModal>
  );
});

const styles = StyleSheet.create({
  handle: {
    backgroundColor: "#334155",
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 40,
    gap: 16,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    flex: 1,
  },
  closeButton: {
    padding: 4,
  },
  message: {
    textAlign: "left",
  },
  input: {
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 24,
    letterSpacing: 8,
    textAlign: "center",
    color: "#111827",
  },
  actions: {
    flexDirection: "row",
    gap: 12,
    marginTop: 8,
  },
  button: {
    flex: 1,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 6,
  },
  cancelButton: {
    backgroundColor: "#EF4444",
  },
  submitButton: {
    backgroundColor: BrandColors.primary,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  submitText: {
    color: "#fff",
  },
  cancelText: {
    color: "#fff",
  },
});
